import orderBy from 'lodash/orderBy';
import { CurrencyBalanceAttributes } from '@loyalty/shared-common/profile-api-client';
import {
  LevelAttributes,
  computeAmountUntilNextLevel,
} from '@loyalty/shared-common/gamification-api-client';
import { defaultCurrencyImage, getLocalizedProperty, uuid } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { GenericComponent } from '../../components-tree';
import { LevelName } from './LevelName';
import { LevelV2Skeleton } from './LevelV2Skeleton';
import { ProgressBar } from './ProgressBar';
import { ReactBootstrap } from '../../ReactBootstrap';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';
import { getClassName } from '../../utils/get-class-name';
import { renderLevelExpiration } from '../../utils/levels-utils';

const CLASSNAME = 'qlf-level';

function computePercentageToNextLevel(
  currentLevel: LevelAttributes | undefined,
  nextLevel: LevelAttributes | undefined,
  currencyBalance: CurrencyBalanceAttributes | undefined,
): number {
  const { current, total } = computeAmountUntilNextLevel(currentLevel, nextLevel, currencyBalance);
  const progress = (current / total) * 100;
  return Math.max(Math.min(progress, 100), 0);
}

function formatAmountToNextLevel(
  amountTowardsNextLevel: number,
  nextLevel: LevelAttributes | undefined,
  totalForNextLevel: number | undefined,
  progressFormat: 'direct' | 'fraction',
): string {
  if (!nextLevel || !totalForNextLevel) {
    return '';
  }
  if (progressFormat === 'fraction') {
    return ` ${amountTowardsNextLevel}/${totalForNextLevel} `;
  } else {
    return ` ${totalForNextLevel - amountTowardsNextLevel} `;
  }
}

export interface LevelComponentAttributes {
  showLevelName: boolean;
  showProgressBar: boolean;
  showAmountUntilNextLevel: boolean;
  showExpiration: boolean;
  progressFormat: 'direct' | 'fraction';
}

type Component = GenericComponent<LevelComponentAttributes>;

type LevelWidgetBackendProps = WidgetProps<Component>;

interface LevelWidgetFrontendProps {
  id: string;
  attributes: Component['attributes'];
  isTowardsFirstLevel: boolean;
  amountUntilNextLevel: number;
  formattedAmountToNextLevel: string;
  expirationString: string;
  levelName?: string;
  nextLevelName?: string;
  levelImage?: string;
  nextLevelImage?: string;
  currencyImage?: string;
  currencyName: string;
  leftStyle: Record<string, any>;
  rightStyle: Record<string, any>;
  progressBarStyle: Record<string, any>;
}

export function serializeLevelV2ComponentProps({
  state,
  options,
  context,
}: LevelWidgetBackendProps): LevelWidgetFrontendProps {
  const { levels, levelConfig, currencies, profile, programId, membership } = state;
  const { locale } = context.i18n;

  const sortedLevels = orderBy(levels, ['minCurrencyAmount']);
  const level = sortedLevels.find(l => l.id === membership.level?.current);

  const nextLevel = sortedLevels.find(
    l => !level || l.minCurrencyAmount > (level.minCurrencyAmount ?? 0),
  );

  const levelImage = getLocalizedProperty(level, 'image', locale);
  const levelName = getLocalizedProperty(level, 'name', locale);
  const currencyBalance = profile.currencies?.find(
    balance => balance.programId === programId && balance.currencyId === levelConfig?.currencyId,
  );
  const currency = currencies.find(c => c.id === levelConfig?.currencyId);
  const currencyImage = getLocalizedProperty(currency, 'image', locale);
  const currencyName = getLocalizedProperty(currency, 'name', locale) || 'Points';
  const { current: amountTowardsNextLevel, total: totalForNextLevel } = computeAmountUntilNextLevel(
    level,
    nextLevel,
    currencyBalance,
  );
  const percentageToNextLevel = computePercentageToNextLevel(level, nextLevel, currencyBalance);
  const nextLevelImage = getLocalizedProperty(nextLevel, 'image', locale);
  const nextLevelName = getLocalizedProperty(nextLevel, 'name', locale);
  const isTowardsFirstLevel = !level && !!nextLevel;

  const expirationString = renderLevelExpiration(
    levelConfig,
    context.i18n,
    { currentLevel: level, membership, nextLevel },
    'portal',
  );

  const formattedAmountToNextLevel = formatAmountToNextLevel(
    amountTowardsNextLevel,
    nextLevel,
    totalForNextLevel,
    options.attributes.progressFormat,
  );

  const amountUntilNextLevel = totalForNextLevel ? totalForNextLevel - amountTowardsNextLevel : 0;
  let rightStyle = {};
  let progressBarStyle = {};
  const leftStyle = { transform: 'rotate(' + percentageToNextLevel * 3.6 + 'deg)' };

  if (percentageToNextLevel <= 50) {
    rightStyle = { display: 'none' };
  } else {
    rightStyle = { transform: 'rotate(180deg)' };
    progressBarStyle = { clip: 'rect(auto, auto, auto, auto)' };
  }

  return {
    attributes: options.attributes,
    id: options.id || options.attributes.id || uuid(),
    isTowardsFirstLevel,
    amountUntilNextLevel,
    formattedAmountToNextLevel,
    expirationString,
    levelName,
    nextLevelName,
    levelImage,
    nextLevelImage,
    currencyImage,
    currencyName,
    leftStyle,
    rightStyle,
    progressBarStyle,
  };
}

function Component({
  id,
  attributes,
  isTowardsFirstLevel,
  amountUntilNextLevel,
  formattedAmountToNextLevel,
  expirationString,
  levelName,
  nextLevelName,
  levelImage,
  nextLevelImage,
  currencyImage,
  currencyName,
  leftStyle,
  rightStyle,
  progressBarStyle,
}: LevelWidgetFrontendProps) {
  const { t } = useTranslation('participant');

  const {
    class: className,
    showLevelName,
    showAmountUntilNextLevel,
    showProgressBar,
    showExpiration,
  } = attributes;

  const mainContentRender = (
    <>
      {(levelImage || nextLevelImage) && (
        <img alt="" src={levelImage ?? nextLevelImage} className={`${CLASSNAME}__image`} />
      )}
    </>
  );

  const progressBarRender = showProgressBar ? (
    <ProgressBar
      progressStyle={progressBarStyle}
      rightStyle={rightStyle}
      leftStyle={leftStyle}
      firstLevel={isTowardsFirstLevel}
    >
      {mainContentRender}
    </ProgressBar>
  ) : (
    <div className={`${CLASSNAME}__alt-container`}>{mainContentRender}</div>
  );

  const amountUntilNextLevelRender = (
    <>
      {showAmountUntilNextLevel && (
        <caption
          className={`${CLASSNAME}__nextlevel_container`}
          aria-label={
            nextLevelName
              ? t('aria.level.untilNextLevel', {
                  amount: amountUntilNextLevel,
                  currencyName,
                  nextLevelName,
                })
              : t('aria.level.untilNoHigherLevel')
          }
        >
          <div aria-hidden>
            <span className={`${CLASSNAME}__nextlevel_amount`}>
              {nextLevelName && formattedAmountToNextLevel}
              {nextLevelName && currencyImage && (
                <img
                  alt={currencyName}
                  src={currencyImage || defaultCurrencyImage}
                  className={`${CLASSNAME}__currency_image`}
                  style={{ display: 'inline-block', marginLeft: '3px' }}
                />
              )}
              {nextLevelName && isTowardsFirstLevel && t('editor.level.amount-until-first-level')}
            </span>
            {nextLevelName && !isTowardsFirstLevel && t('editor.level.amount-until-next-level')}
            {!isTowardsFirstLevel && (
              <div className={`${CLASSNAME}__nextlevel`}>
                {nextLevelName ?? ''}
                {!nextLevelName && levelName && t('editor.level.max-level')}
                {!nextLevelName && !levelName && t('editor.level.no-config')}
                {nextLevelImage && (
                  <img
                    alt=""
                    src={nextLevelImage}
                    className={`${CLASSNAME}__nextlevel_image`}
                    style={{ display: 'inline-block', marginLeft: '3px' }}
                  />
                )}
              </div>
            )}
          </div>
        </caption>
      )}
    </>
  );

  return (
    <section
      className={getClassName([CLASSNAME, className])}
      aria-label={t('aria.level.label')}
      aria-description={t('aria.level.description')}
    >
      {isTowardsFirstLevel && amountUntilNextLevelRender}
      {progressBarRender}
      {showLevelName && <LevelName levelName={levelName} nextLevelName={nextLevelName} />}
      {!isTowardsFirstLevel && amountUntilNextLevelRender}
      {showExpiration && <div className={`${CLASSNAME}__expiration`}>{expirationString}</div>}
    </section>
  );
}

export function LevelV2Component(props: LevelWidgetFrontendProps) {
  return (
    <TranslationProvider loader={<LevelV2Skeleton />}>
      <Component {...props} />
    </TranslationProvider>
  );
}

export function LevelV2Renderer(props: LevelWidgetBackendProps, document?: Document): string {
  const reactProps = serializeLevelV2ComponentProps(props);
  return renderToString(
    <ReactBootstrap
      component="LevelV2Component"
      props={reactProps}
      document={document}
      componentType="level-v2"
    />,
  );
}
